import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Form, Text } from 'rsuite';
import { CheckType } from 'schema-typed';
import { AddressLookupAccepter, AddressOption } from '../AddressLookupAccepter';
import { lookupAddress } from '../../../../api/apiAccess';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useResize } from '../../../../hooks/useResize';
import { LookupAddressResponse } from 'src/types/address';
import { parseSmartyResultToAddress } from './utils';

const DEBOUNCE_DELAY = 500;
const MINIMUM_SEARCH_TERM_LENGTH = 3;
const noop = () => {};

export interface UnrestrictedAddressLookupProps {
  checkTrigger?: 'change' | 'blur' | 'none';
  disabled?: boolean;
  errorMessage?: string;
  hideErrorIcon?: boolean;
  label: string;
  name: string;
  onChange?: (value: string | AddressOption) => void;
  onTextChanged?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationRule?: CheckType<unknown, any>;
  value?: string | AddressOption;
}

const UnrestrictedAddressLookup = ({
  disabled = false,
  errorMessage,
  hideErrorIcon = false,
  label,
  name,
  onTextChanged = noop,
  validationRule,
  value,
  ...rest
}: UnrestrictedAddressLookupProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState('0px');
  const { resizeTrigger } = useResize();
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      setMenuWidth(`${containerRef.current.clientWidth}px`);
    }
  }, [resizeTrigger]);

  const hasError = useMemo(() => !!errorMessage, [errorMessage]);

  const realSearchTerm = useDebounce(
    typeof value === 'string' ? value : value?.value || '',
    DEBOUNCE_DELAY
  );

  const debouncedSearch = useCallback(
    async (term: string | AddressOption) => {
      const text = typeof term === 'string' ? term : term?.value || '';
      onTextChanged();
      if (text.length < MINIMUM_SEARCH_TERM_LENGTH) {
        setAddressOptions([]);
        return;
      }
      setLoading(true);
      const { data } = (await lookupAddress(text)) as {
        data: LookupAddressResponse;
      };
      const parsedData = data?.suggestions?.map((suggestion) =>
        parseSmartyResultToAddress(suggestion)
      );
      setAddressOptions(parsedData ?? []);
      setLoading(false);
    },
    [onTextChanged]
  );

  useEffect(() => {
    debouncedSearch(realSearchTerm);
  }, [realSearchTerm, debouncedSearch]);

  return (
    <Form.Group
      controlId={name}
      data-testid="unrestricted-address-lookup"
      style={{ marginBottom: 0 }}
    >
      <Form.ControlLabel>
        <Text
          as="h5"
          className="text-bold"
        >
          {label}
        </Text>
      </Form.ControlLabel>
      <div ref={containerRef}>
        <Form.Control
          style={{ maxWidth: menuWidth }}
          {...rest}
          accepter={AddressLookupAccepter}
          className={disabled ? 'disabled-input' : undefined}
          data={addressOptions}
          disabled={disabled}
          errorText={errorMessage}
          hasError={hasError}
          hideErrorIcon={hideErrorIcon}
          loading={loading}
          name={name}
          rule={validationRule}
          value={value}
        />
      </div>
    </Form.Group>
  );
};

export default UnrestrictedAddressLookup;
